import React, { useEffect, useCallback, useState } from 'react'
import { CircularProgress } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { ErrorMessage } from 'core/components/validatedForm/ErrorMessage'
import Button from 'core/elements/button'
import Modal from 'core/elements/modal'
import Text from 'core/elements/Text'
import { getVmEvents, getVmEventDetails } from './actions'
import CodeBlock from 'core/components/CodeBlock'

const useStyles = makeStyles((theme) => ({
  progressContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  errorBlock: {
    color: 'red',
    backgroundColor: '#fcebeb',
  },
  retry: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
  },
  centerText: {
    display: 'grid',
    justifyContent: 'center',
  },
}))

interface Props {
  vm: any
  onClose: () => void
}

// Note: Plans for backend to eventually surface this information for us... then it would
// be as easy as displaying the VM property
const FailedMigrationReasonDialog = ({ vm, onClose }: Props) => {
  const classes = useStyles()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [errorEvent, setErrorEvent] = useState(null)

  const getEvents = useCallback(
    async (id) => {
      setLoading(true)
      try {
        const events = await getVmEvents({ id })
        // get first in the list which should be the most recent one
        const recentMigrationEvent = events.find((event) =>
          ['live-migration', 'migration'].includes(event.action),
        )
        if (!recentMigrationEvent) {
          setError('No migration task found')
          return
        }
        const details = await getVmEventDetails({
          vmId: vm.id,
          requestId: recentMigrationEvent?.request_id,
        })
        const errorTask = details?.events?.find((event) => event?.result === 'Error')
        if (!errorTask) {
          setError('No migration task found')
        }
        setErrorEvent(errorTask)
      } catch (e) {
        setError('Error retrieving migration failure details')
      } finally {
        setLoading(false)
      }
    },
    [vm?.id],
  )

  useEffect(() => {
    getEvents(vm?.id)
  }, [vm?.id, getEvents])

  return (
    <Modal
      open
      onClose={onClose}
      maxWidth={1000}
      title="Migration Failure Details"
      panel="dialog"
      footer={
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      }
    >
      <>
        {loading ? (
          <div className={classes.progressContainer}>
            <CircularProgress size={20} />
            <Text variant="body2">
              Retrieving migration failure reason for <b>{vm?.name || vm?.id}</b>
            </Text>
          </div>
        ) : (
          <>
            {!!errorEvent?.traceback && (
              <>
                <CodeBlock className={classes.errorBlock}>{errorEvent?.details}</CodeBlock>
                <CodeBlock className={classes.errorBlock}>{errorEvent?.traceback}</CodeBlock>
              </>
            )}
            {error && !errorEvent?.traceback && (
              <div className={classes.centerText}>
                <div className={classes.retry}>
                  <Button variant="secondary" icon="sync" onClick={() => getEvents(vm?.id)}>
                    Retry
                  </Button>
                </div>
                <ErrorMessage>{error}</ErrorMessage>
              </div>
            )}
          </>
        )}
      </>
    </Modal>
  )
}

export default FailedMigrationReasonDialog
